import { Link } from "react-router-dom";
import { Play, Radio, Clapperboard } from "lucide-react";
import { VideoPlayer } from "../VideoPlayer";
import { useI18n } from "../../lib/i18n";

const ChannelQuicklist = ({ channels, t }) => (
  <aside className="glass rounded-xl p-5">
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-display text-2xl">{t("home.live")}</h2>
      <Link to="/channels" className="text-xs uppercase tracking-wider text-[color:var(--text-muted)] hover:text-white">{t("home.all")}</Link>
    </div>
    <ul className="space-y-1 stagger">
      {channels.slice(0, 8).map((c, i) => (
        <li key={c.id}>
          <Link data-testid={`channel-row-${c.slug}`} to={`/channel/${c.id}`} className="flex items-center gap-4 px-3 py-2 rounded-lg hover:bg-[color:var(--bg-elev-2)] transition-colors">
            <span className="font-mono text-xs text-[color:var(--text-muted)] w-8">{String(i + 1).padStart(2, "0")}</span>
            <span className="w-1.5 h-8 rounded-full" style={{ background: c.color }} />
            <span className="flex-1">
              <div className="font-medium">{c.name}</div>
              <div className="text-xs text-[color:var(--text-muted)]">{c.category}</div>
            </span>
            <span className="live-dot" />
          </Link>
        </li>
      ))}
    </ul>
  </aside>
);

const Marquee = ({ channels, t }) => (
  <div className="marquee border-y border-[color:var(--border)] py-3 text-sm uppercase tracking-[0.2em] text-[color:var(--text-muted)]">
    <div className="marquee-inner">
      {["marquee-a", "marquee-b"].map((k) => (
        <span key={k}>
          {channels.map(c => (
            <span key={c.id} className="mx-8">
              <span className="inline-block w-1.5 h-1.5 rounded-full align-middle mr-2" style={{ background: c.color }} />
              {c.name} · {t("live.live")}
            </span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

export const HeroSection = ({ featured, channels, onReplayIntro }) => {
  const { t } = useI18n();
  return (
    <section className="relative overflow-hidden border-b border-[color:var(--border)]">
      <div className="max-w-[1400px] mx-auto px-6 pt-10 pb-12 grid lg:grid-cols-[1.6fr_1fr] gap-8">
        <div>
          <div className="flex items-center gap-3 mb-4 text-xs uppercase tracking-[0.25em] text-[color:var(--accent)]">
            <span className="live-dot" /> {t("home.onair")}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none mb-4" data-testid="hero-title">
            {featured?.title || t("home.welcome")}
          </h1>
          <p className="text-[color:var(--text-muted)] max-w-xl mb-6">{t("home.intro")}</p>
          <div className="crt-tv" data-testid="crt-hero-tv">
            <span className="crt-tv-label">FEDEVO 2.0</span>
            <span className="crt-knob l" />
            <span className="crt-knob r" />
            <div className="crt-screen">
              <VideoPlayer video={featured} autoplay={false} />
            </div>
          </div>
          {featured && (
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to={`/channel/${featured.channel_id}`} data-testid="hero-tunein-btn" className="inline-flex items-center gap-2 bg-[color:var(--accent)] hover:brightness-110 text-white px-5 py-2.5 rounded-full text-sm uppercase tracking-wider font-semibold transition">
                <Radio className="w-4 h-4" /> {t("home.tunein")}
              </Link>
              <Link to={`/watch/${featured.id}`} className="inline-flex items-center gap-2 border border-[color:var(--border)] hover:border-white px-5 py-2.5 rounded-full text-sm uppercase tracking-wider transition">
                <Play className="w-4 h-4" /> {t("home.watchfull")}
              </Link>
              <button type="button" onClick={onReplayIntro} data-testid="hero-replay-intro" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--text-muted)] hover:text-[color:var(--accent)] transition-colors px-2">
                <Clapperboard className="w-4 h-4" /> Revoir l'intro
              </button>
            </div>
          )}
        </div>
        <ChannelQuicklist channels={channels} t={t} />
      </div>
      <Marquee channels={channels} t={t} />
    </section>
  );
};
